import * as React from 'react';

import { cn } from '@/lib/cn';

type AvatarImageLoadingStatus = 'idle' | 'loading' | 'loaded' | 'error';

type AvatarContextValue = {
  imageLoadingStatus: AvatarImageLoadingStatus;
  setImageLoadingStatus: React.Dispatch<
    React.SetStateAction<AvatarImageLoadingStatus>
  >;
};

const AvatarContext = React.createContext<AvatarContextValue | null>(null);

function useAvatarContext(componentName: string) {
  const context = React.useContext(AvatarContext);

  if (!context) {
    throw new Error(`${componentName} must be used within Avatar`);
  }

  return context;
}

const Avatar = React.forwardRef<
  HTMLSpanElement,
  React.ComponentPropsWithoutRef<'span'>
>(({ className, ...props }, ref) => {
  const [imageLoadingStatus, setImageLoadingStatus] =
    React.useState<AvatarImageLoadingStatus>('idle');

  return (
    <AvatarContext.Provider
      value={{ imageLoadingStatus, setImageLoadingStatus }}
    >
      <span
        ref={ref}
        className={cn(
          'relative flex shrink-0 overflow-hidden rounded-full',
          className,
        )}
        {...props}
      />
    </AvatarContext.Provider>
  );
});

const AvatarImage = React.forwardRef<
  HTMLImageElement,
  React.ComponentPropsWithoutRef<'img'>
>(({ className, onLoad, onError, ...props }, ref) => {
  const { imageLoadingStatus, setImageLoadingStatus } =
    useAvatarContext('AvatarImage');

  React.useEffect(() => {
    if (props.src) {
      setImageLoadingStatus('loading');
      return;
    }

    setImageLoadingStatus('error');
  }, [props.src, setImageLoadingStatus]);

  return (
    <img
      ref={ref}
      className={cn(
        'aspect-square size-full object-cover',
        imageLoadingStatus === 'loaded' ? 'block' : 'hidden',
        className,
      )}
      onLoad={(event) => {
        setImageLoadingStatus('loaded');
        onLoad?.(event);
      }}
      onError={(event) => {
        setImageLoadingStatus('error');
        onError?.(event);
      }}
      {...props}
    />
  );
});

const AvatarFallback = React.forwardRef<
  HTMLSpanElement,
  React.ComponentPropsWithoutRef<'span'>
>(({ className, ...props }, ref) => {
  const { imageLoadingStatus } = useAvatarContext('AvatarFallback');

  if (imageLoadingStatus === 'loaded') {
    return null;
  }

  return (
    <span
      ref={ref}
      className={cn(
        'flex size-full items-center justify-center rounded-full',
        className,
      )}
      {...props}
    />
  );
});

export { Avatar, AvatarFallback,AvatarImage };
